type SafeStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  keys: () => string[];
};

const noopStorage: SafeStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
  keys: () => [],
};

let cachedLocalStorage: SafeStorage | null = null;
let cachedSessionStorage: SafeStorage | null = null;

function testStorage(storage: Storage): boolean {
  try {
    const testKey = "__storage_test__";
    storage.setItem(testKey, testKey);
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

function createSafeStorage(storage: Storage): SafeStorage {
  return {
    getItem: (key) => {
      try {
        return storage.getItem(key);
      } catch {
        return null;
      }
    },
    setItem: (key, value) => {
      try {
        storage.setItem(key, value);
      } catch {
      }
    },
    removeItem: (key) => {
      try {
        storage.removeItem(key);
      } catch {
      }
    },
    keys: () => {
      try {
        const keys: string[] = [];
        for (let index = 0; index < storage.length; index += 1) {
          const key = storage.key(index);
          if (key !== null) keys.push(key);
        }
        return keys;
      } catch {
        return [];
      }
    },
  };
}

export function safeLocalStorage(): SafeStorage {
  if (cachedLocalStorage !== null) return cachedLocalStorage;

  try {
    if (typeof window === "undefined") {
      cachedLocalStorage = noopStorage;
    } else if (testStorage(window.localStorage)) {
      cachedLocalStorage = createSafeStorage(window.localStorage);
    } else {
      cachedLocalStorage = noopStorage;
    }
  } catch {
    cachedLocalStorage = noopStorage;
  }

  return cachedLocalStorage;
}

export function safeSessionStorage(): SafeStorage {
  if (cachedSessionStorage !== null) return cachedSessionStorage;

  try {
    if (typeof window === "undefined") {
      cachedSessionStorage = noopStorage;
    } else if (testStorage(window.sessionStorage)) {
      cachedSessionStorage = createSafeStorage(window.sessionStorage);
    } else {
      cachedSessionStorage = noopStorage;
    }
  } catch {
    cachedSessionStorage = noopStorage;
  }

  return cachedSessionStorage;
}

export function safeStorage(
  type: "localStorage" | "sessionStorage",
): SafeStorage {
  return type === "localStorage" ? safeLocalStorage() : safeSessionStorage();
}

export function hasAccessibleStorage(
  type: "localStorage" | "sessionStorage",
): boolean {
  try {
    if (typeof window === "undefined") {
      return false;
    }

    const storage =
      type === "localStorage" ? window.localStorage : window.sessionStorage;
    return testStorage(storage);
  } catch {
    return false;
  }
}
